import { DateTime } from 'luxon'
import { ShadowOptin, SODesktopScreenSlugs, SOSchemaRule } from '@/data'

import { SOComparisons, SOFieldTypes, SOStoreTypes } from '@/data'
import { SurveyQuestion } from '@/data/Survey'
import { PendingAcquisitionUser } from '@/dataV3/User/PendingAcquisitionUser'
import { PendingAcquisitionAnimal } from '@/dataV3/Animal/PendingAcquisitionAnimal'
import { User } from '@/dataV3/User/User'
import { Petsitter } from '@/dataV3/PetSitter/Petsitter'

const compareField = (field: any | undefined, rule: SOSchemaRule<any>): boolean => {
  const { comparison, fieldType, value: valueStr } = rule || {}

  const errorPrefix = `[${rule.field}] -`

  if (comparison === SOComparisons.NULL_OR_UNDEFINED) {
    return field === undefined || field === null
  }

  switch (fieldType) {
    case SOFieldTypes.BOOLEAN: {
      if (field === undefined || field === null) {
        return comparison === SOComparisons.NOT_EQUAL
      }
      if (typeof field !== 'boolean') {
        throw new Error(`${errorPrefix} Bad comparison, field is '${typeof field}', expect 'boolean'`)
      }
      const value = valueStr === 'true'
      switch (comparison) {
        case SOComparisons.STRICT_EQUAL:
          return field === value
        case SOComparisons.NOT_EQUAL:
          return field !== value
        default:
          throw new Error(`${errorPrefix} Comparison '${comparison}' not supported for '${fieldType}'`)
      }
    }
    case SOFieldTypes.NUMBER: {
      if (field === undefined || field === null) {
        return comparison === SOComparisons.NOT_EQUAL
      }
      if (typeof field !== 'number') {
        throw new Error(`${errorPrefix} Bad comparison, field is '${typeof field}', expect 'number'`)
      }
      const value = Number.parseFloat(valueStr)
      if (Number.isNaN(value)) {
        throw new Error(`${errorPrefix} value ${valueStr} is not a number`)
      }
      switch (comparison) {
        case SOComparisons.STRICT_EQUAL:
          return field === value
        case SOComparisons.NOT_EQUAL:
          return field !== value
        case SOComparisons.LESS_THAN:
          return field < value
        case SOComparisons.LESS_THAN_OR_EQUAL:
          return field <= value
        case SOComparisons.MORE_THAN:
          return field > value
        case SOComparisons.MORE_THAN_OR_EQUAL:
          return field >= value
        default:
          throw new Error(`${errorPrefix} Comparison '${comparison}' not supported for '${fieldType}'`)
      }
    }
    case SOFieldTypes.STRING: {
      if (field === undefined || field === null) {
        return comparison === SOComparisons.NOT_EQUAL
      }
      if (typeof field !== 'string') {
        throw new Error(`${errorPrefix} Bad comparison, field is '${typeof field}', expect 'string'`)
      }

      const value = valueStr
      switch (comparison) {
        case SOComparisons.STRICT_EQUAL:
          return field === value
        case SOComparisons.NOT_EQUAL:
          return field !== value
        case SOComparisons.REGEX: {
          const regex = new RegExp(value)
          return regex.test(field)
        }
        default:
          throw new Error(`${errorPrefix} Comparison '${comparison}' not supported for '${fieldType}'`)
      }
    }
    case SOFieldTypes.DATE: {
      if (field === undefined || field === null) {
        return comparison === SOComparisons.NOT_EQUAL
      }

      let fieldMoment
      if (field instanceof Date) fieldMoment = DateTime.fromJSDate(field)
      if (typeof field === 'string') fieldMoment = DateTime.fromISO(field)

      if (!fieldMoment || !fieldMoment.isValid()) {
        throw new Error(
          `${errorPrefix} Bad comparison, field '${typeof field}' is not a Date object or is not a valid Date '${field}'`,
        )
      }

      const durationFormatRegex = /^([0-9]+)(M|D|Y)$/gm
      const res = durationFormatRegex.exec(valueStr)

      let value
      if (res && res.length > 2) {
        const val = parseInt(res[1], 10)
        let unit
        switch (res[2]) {
          case 'D':
            unit = 'days'
            break
          case 'M':
            unit = 'months'
            break
          case 'Y':
            unit = 'years'
            break
          default:
            break
        }

        value = DateTime.now().startOf('day').diff(fieldMoment, unit)

        switch (comparison) {
          case SOComparisons.STRICT_EQUAL:
            return value === 0
          case SOComparisons.NOT_EQUAL:
            return value !== 0
          case SOComparisons.LESS_THAN:
            return value < val
          case SOComparisons.LESS_THAN_OR_EQUAL:
            return value <= val
          case SOComparisons.MORE_THAN:
            return value > val
          case SOComparisons.MORE_THAN_OR_EQUAL:
            return value >= val
          default:
            throw new Error(`${errorPrefix} Comparison '${comparison}' not supported for '${fieldType}'`)
        }
      } else {
        value = DateTime.fromISO(valueStr).toFormat('YYYY-MM-DD')
        if (!value.isValid()) {
          throw new Error(`${errorPrefix} value ${valueStr} is not a valid Date`)
        }

        switch (comparison) {
          case SOComparisons.STRICT_EQUAL:
            return fieldMoment === value
          case SOComparisons.NOT_EQUAL:
            return fieldMoment !== value
          case SOComparisons.LESS_THAN:
            return fieldMoment < value
          case SOComparisons.LESS_THAN_OR_EQUAL:
            return fieldMoment <= value
          case SOComparisons.MORE_THAN:
            return fieldMoment > value
          case SOComparisons.MORE_THAN_OR_EQUAL:
            return fieldMoment >= value
          default:
            throw new Error(`${errorPrefix} Comparison '${comparison}' not supported for '${fieldType}'`)
        }
      }
    }
    case SOFieldTypes.ARRAY: {
      if (field === undefined || field === null) {
        return [SOComparisons.EMPTY, SOComparisons.EXCLUDE, SOComparisons.COUNT_LESS_THAN].includes(comparison)
      }
      if (!Array.isArray(field)) {
        throw new Error(`${errorPrefix} Bad comparison, field is not an array`)
      }
      switch (comparison) {
        case SOComparisons.SOME:
          return field.some((it) => it === valueStr)
        case SOComparisons.EVERY:
          return field.every((it) => it === valueStr)
        case SOComparisons.EXCLUDE:
          return field.every((it) => it !== valueStr)
        case SOComparisons.EMPTY:
          return field.length === 0
        case SOComparisons.COUNT_LESS_THAN: {
          const value = Number.parseInt(valueStr, 10)
          if (Number.isNaN(value)) {
            throw new Error(`${errorPrefix} value ${valueStr} is not a number for array COUNT`)
          }
          return field.length < value
        }
        case SOComparisons.COUNT_MORE_THAN: {
          const value = Number.parseInt(valueStr, 10)
          if (Number.isNaN(value)) {
            throw new Error(`${errorPrefix} value ${valueStr} is not a number for array COUNT`)
          }
          return field.length > value
        }
        default:
          throw new Error(`${errorPrefix} Comparison '${comparison}' not supported for '${fieldType}'`)
      }
    }
    default:
      throw new Error(`${errorPrefix} fieldType '${fieldType}' not supported`)
  }
}

const extractField = <T extends object>(item: T | undefined | null, mainKey: string): object | undefined => {
  const keys = mainKey.split('.')

  let tempItem: object | undefined | null = item
  keys.forEach((key) => {
    if (tempItem && key in tempItem) {
      tempItem = tempItem[key]
    } else {
      tempItem = undefined
    }
  })

  return tempItem || undefined
}

const filterShadowOptinsToApply = (
  shadowOptins: ShadowOptin[] | undefined,
  user: PendingAcquisitionUser | undefined | null,
  screenSlug: SODesktopScreenSlugs,
  options: {
    currentAnimal?: PendingAcquisitionAnimal | undefined
    screenValues?: Record<string, boolean> | undefined
    enableLogs?: boolean | undefined
  },
): ShadowOptin[] => {
  const { currentAnimal, screenValues = {}, enableLogs = false } = options

  const finalShadowOptins: ShadowOptin[] = (shadowOptins || []).filter((shadowOptin: ShadowOptin) => {
    const { screen = [], storeRule = [], screenRule = [], name } = shadowOptin || {}

    let newScreen = screen
    if (typeof screen === 'object') {
      newScreen = Object.keys(screen).map((screenKey) => screen[screenKey])
    }

    if (enableLogs) {
      console.group(`Treating rule ${name}`)
    }

    const isScreenOK = newScreen.some((it) => it.slug === screenSlug)
    if (!isScreenOK) {
      if (enableLogs) {
        console.log(`screen not allowed ${screenSlug}`)
        console.groupEnd()
      }
      return false
    }

    const isStoreRuleOK = storeRule.every((rule) => {
      let field
      switch (rule.type) {
        case SOStoreTypes.User: {
          field = extractField(user, rule.field)
          break
        }
        case SOStoreTypes.Animal: {
          field = extractField(currentAnimal, rule.field)
          break
        }
        default:
          console.log(`ShadowOptinsH: type ignored: ${rule.type}`)
          field = undefined
          break
      }

      try {
        const result = compareField(field, rule)

        if (!result && enableLogs) {
          console.log(`Field ${rule.field} not satisfied`, field)
        }

        return result
      } catch (err) {
        console.warn('[SOHelper] - Cannot compare fields', err)
        return false
      }
    })

    if (!isStoreRuleOK) {
      if (enableLogs) {
        console.log('Some store rule not satisfied')
        console.groupEnd()
      }
      return false
    }

    const isScreenRuleOK = screenRule.every((rule) => {
      const field = screenValues[rule.field]
      try {
        const result = compareField(field, rule)
        if (!result && enableLogs) {
          console.log(`Field ${rule.field} not satisfied`, screenValues)
        }
        return result
      } catch (err) {
        console.warn('[SOHelper] - Cannot compare fields', err)
        return false
      }
    })

    if (enableLogs) {
      if (!isScreenRuleOK) {
        console.log('Some screen rule not satisfied')
      } else {
        console.log('OK !')
      }

      console.groupEnd()
    }

    return isScreenRuleOK
  })

  return finalShadowOptins
}

const filterSurveyQuestionToApply = (
  surveyQuestions: SurveyQuestion[],
  user: User | Petsitter | null,
  currentAnimal?: PendingAcquisitionAnimal | null,
): SurveyQuestion[] => {
  const finalSurveyQuestions: SurveyQuestion[] = []

  surveyQuestions.forEach((surveyQuestion) => {
    const { storeRule = [] } = surveyQuestion || {}

    if (!storeRule.length) {
      finalSurveyQuestions.push(surveyQuestion)
      return
    }

    const isStoreRuleOK = storeRule.every((rule) => {
      let field
      switch (rule.type) {
        case SOStoreTypes.User: {
          field = extractField(user, rule.field)
          break
        }
        case SOStoreTypes.Animal: {
          field = extractField(currentAnimal, rule.field)
          break
        }
        default:
          console.log(`ShadowOptinsH: type ignored: ${rule.type}`)
          field = undefined
          break
      }

      return compareField(field, rule)
    })

    if (isStoreRuleOK) {
      finalSurveyQuestions.push(surveyQuestion)
    }
  })

  return finalSurveyQuestions
}

const EMTShadowOptinsHelper = { filterShadowOptinsToApply, filterSurveyQuestionToApply }

export default EMTShadowOptinsHelper
